// @ts-nocheck
// TypeScript is ignoring this file. If you are editing this file you must fix the type issues.

import React, { useEffect, useState } from 'react';
import format from 'date-fns/format';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import { LiliButton, LiliButtonType } from '@lili/lili-frontend-common';
import {
  getFutureDateFromSpecificDate,
  getPriorDateFromSpecificDate,
} from '../../../../../../helpers/datesAndTime.helper';
import { convertValueToUSD } from '../../../../../../helpers/currency.helper';
import { INVOICE_STATUSES, PAYMENT_METHODS_DROPDOWN_OPTIONS } from '../../../consts/consts';
import { addInvoicePaymentAPIAction, markAsPaidAPIAction } from '../../../api/markAsPaidAPIAction';
import { HUB_MODALS } from '../../InvoiceHubModals';
import { useStore } from '../../../../../../store';
import { InvoiceMarkAsPaidSuccess } from '../../Toasts/InvoiceMarkAsPaidSuccess';
import { ReactComponent as RedErrorIcon } from '../../../../../../assets/svg/invoices-icons/icon-red-error.svg';

const INPUT_STYLE =
  'cursor-pointer w-full h-[54px] rounded-lg border-[1px] border-bw06 pt-[5px] pr-[14px] pb-[8px] pl-[16px]';
const INPUT_TEXT_STYLE = 'font-metricR text-xxs text-bw06';

interface IProps {
  onClose: () => void;
  invoiceAmount: number;
  externalId: string;
  invoiceStatus: string;
}

type SelectedPaymentMethodType = {
  value: string;
  text: string;
  icon: any;
};

const defaultSelectedPaymentMethod = {
  value: '',
  text: 'INVOICE_HUB_MODALS.MARK_AS_PAID.PAYMENT_METHOD_DROPDOWN_DEFAULT',
  icon: '',
};

export const MarkAsPaid = ({ onClose, invoiceAmount, externalId, invoiceStatus }: IProps) => {
  const { customerId } = useStore((state) => state.user);
  const setActiveModal = useStore((state) => state.setActiveModal);
  const removeInvoiceFromList = useStore((state) => state.removeInvoiceFromList);
  const setError = useStore((state) => state.setError);
  const { t } = useTranslation();
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const [showPaymentMethodsDropdown, setShowPaymentMethodsDropdown] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<SelectedPaymentMethodType>(defaultSelectedPaymentMethod);
  const [selectedDateError, setSelectedDateError] = useState<string>('');
  const [selectedPaymentMethodError, setSelectedPaymentMethodError] = useState<string>('');
  const handleDateSelected = (date: Date) => {
    setSelectedDate(date);
    setShowDatePicker(false);
  };
  const handleDateInputClick = () => {
    setShowDatePicker(!showDatePicker);
  };
  const handlePaymentMethodsDDClick = () => {
    setShowPaymentMethodsDropdown(!showPaymentMethodsDropdown);
  };
  const handlePaymentMethodOptionClick = (option: SelectedPaymentMethodType) => {
    setSelectedPaymentMethod(option);
    setShowPaymentMethodsDropdown(false);
  };
  const isPaymentDateValid = () => {
    let isValid = true;
    if (!selectedDate) {
      setSelectedDateError(t('INVOICE_HUB_MODALS.MARK_AS_PAID.ERRORS.INVALID_DATE'));
      isValid = false;
    } else {
      setSelectedDateError('');
    }
    return isValid;
  };
  const isPaymentMethodValid = () => {
    let isValid = true;
    if (!selectedPaymentMethod?.value) {
      setSelectedPaymentMethodError(t('INVOICE_HUB_MODALS.MARK_AS_PAID.ERRORS.INVALID_PAYMENT_METHOD'));
      isValid = false;
    } else {
      setSelectedPaymentMethodError('');
    }
    return isValid;
  };
  const areInputsValid = () => {
    let isDateValid = true;
    let isMethodValid = true;
    isDateValid = isPaymentDateValid();
    isMethodValid = isPaymentMethodValid();
    return isDateValid && isMethodValid;
  };
  const handleSubmit = async () => {
    if (areInputsValid()) {
      onClose();
      setActiveModal(HUB_MODALS.SPINNER);
      try {
        await addInvoicePaymentAPIAction(customerId, externalId, {
          amount: invoiceAmount,
          paymentTime: selectedDate || new Date(),
          paymentMethod: selectedPaymentMethod.value,
        });
        const response = await markAsPaidAPIAction(customerId, externalId);
        setActiveModal(HUB_MODALS.NONE);
        if (response?.invoiceStatus === INVOICE_STATUSES.PAID) {
          toast(<InvoiceMarkAsPaidSuccess />, {
            position: toast.POSITION.TOP_CENTER,
            bodyClassName: 'p-0',
            className: 'w-[350px] h-[60px] bg-bw08 p-0',
          });
          removeInvoiceFromList(invoiceStatus, externalId);
        }
      } catch (error: any) {
        setError(error);
        setActiveModal(HUB_MODALS.ERROR);
      }
    }
  };
  useEffect(() => {
    if (selectedDate) isPaymentDateValid();
  }, [selectedDate]);
  useEffect(() => {
    if (selectedPaymentMethod?.value) isPaymentMethodValid();
  }, [selectedPaymentMethod]);
  return (
    <div
      data-testid='invoiceHub-markAsPaid-main'
      className='justify-between flex flex-nowrap flex-col h-[532px] w-[568px] bg-bw00 rounded-lg border-[1px] border-bw03 px-[24px] pt-[20px] pb-[24px] box-border'
    >
      <div>
        <div className='font-metricRMedium text-lg text-bw08 leading-6'>
          {t('INVOICE_HUB_MODALS.MARK_AS_PAID.TITLE')}
        </div>
        <div className='font-metricR text-md text-bw07 leading-6'>{t('INVOICE_HUB_MODALS.MARK_AS_PAID.SUBTITLE')}</div>
        <div className='flex mt-[20px]'>
          <div className='font-metricR text-md text-bw07 leading-6'>{t('INVOICE_HUB_MODALS.MARK_AS_PAID.BALANCE')}</div>
          <div
            data-testid='invoiceHub-markAsPaid-amount'
            className='ml-[5px] font-metricRMedium text-md text-bw08 leading-6'
          >
            {`${convertValueToUSD(invoiceAmount, 2)}`}
          </div>
        </div>
        <div className='relative'>
          <div
            data-testid='invoiceHub-markAsPaid-selectedDate'
            className={`${INPUT_STYLE} mt-[20px] ${selectedDateError ? 'border-red02' : 'mb-[20px]'}`}
            onClick={handleDateInputClick}
            onKeyDown={handleDateInputClick}
            role='button'
            tabIndex={0}
          >
            <div className={`${INPUT_TEXT_STYLE}`}>{t('INVOICE_HUB_MODALS.MARK_AS_PAID.LABEL_DATE')}</div>
            <div className='font-metricR text-bw08'>
              {selectedDate ? format(selectedDate, 'MMM dd, yyyy') : 'DD/MM/YY'}
            </div>
          </div>
          {selectedDateError && (
            <div data-testid='invoiceHub-markAsPaid-errorDate' className='my-[3px] flex items-center'>
              <RedErrorIcon />
              <div className='ml-[4px] font-metricRMedium text-red02 text-sm'>{t(selectedDateError)}</div>
            </div>
          )}
          {showDatePicker && (
            <div
              data-testid='invoiceHub-markAsPaid-datePicker'
              className='absolute rounded-lg left-0 bottom-[-314px] w-fit shadow-chatBotButton bg-bw00 min-w-[260px] z-[100]'
            >
              <DatePicker
                onChange={handleDateSelected}
                onClickOutside={() => setShowDatePicker(false)}
                selected={selectedDate}
                minDate={getPriorDateFromSpecificDate(180, new Date())}
                maxDate={getFutureDateFromSpecificDate(180, new Date())}
                dropdownMode='select'
                showMonthDropdown
                showYearDropdown
                inline
              />
            </div>
          )}
        </div>
        <div className='relative'>
          <div
            data-testid='invoiceHub-markAsPaid-selectedPaymentMethod'
            className={`${INPUT_STYLE} ${selectedPaymentMethodError ? 'border-red02' : ''}`}
            onClick={handlePaymentMethodsDDClick}
            onKeyDown={handlePaymentMethodsDDClick}
            role='button'
            tabIndex={0}
          >
            <div className={`${INPUT_TEXT_STYLE}`}>{t('INVOICE_HUB_MODALS.MARK_AS_PAID.LABEL_PAYMENT_METHOD')}</div>
            <div className='font-metricR text-bw08'>{t(selectedPaymentMethod.text)}</div>
          </div>
          {selectedPaymentMethodError && (
            <div data-testid='invoiceHub-markAsPaid-errorPaymentMethod' className='my-[3px] flex items-center'>
              <RedErrorIcon />
              <div className='ml-[4px] font-metricRMedium text-red02 text-sm'>{t(selectedPaymentMethodError)}</div>
            </div>
          )}
          {showPaymentMethodsDropdown && (
            <div
              data-testid='invoiceHub-markAsPaid-paymentMethodDropdown'
              className='absolute rounded-lg left-0 bottom-[-242px] w-fit shadow-chatBotButton bg-bw00 min-w-[260px]'
            >
              <ul>
                {PAYMENT_METHODS_DROPDOWN_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  return (
                    <li
                      data-testid={`paymentMethod-${option.value.toLowerCase()}`}
                      className='w-[516px] h-[40px] hover:cursor-pointer hover:bg-bw02 last:rounded-b-lg first:rounded-t-lg'
                      key={`payment_method_${option.value}`}
                    >
                      <button
                        className='w-full h-full flex items-center pl-[15px] py-[9px] '
                        type='button'
                        onClick={() => handlePaymentMethodOptionClick(option)}
                      >
                        <Icon className='w-[18px] h-[18px]' />
                        <div className='font-metricR text-bw08 ml-[10px] text-md leading-4'>{t(option.text)}</div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
      <div className='flex justify-end'>
        <LiliButton
          id='invoiceHub-markAsPaid-buttonCancel'
          testId='invoiceHub-markAsPaid-buttonCancel'
          onClick={onClose}
          type={LiliButtonType.SECONDARY}
          title={t('INVOICE_HUB_MODALS.MARK_AS_PAID.BUTTON_CANCEL')}
          className='h-[44px] w-[103px]'
        />
        <LiliButton
          id='invoiceHub-markAsPaid-recordAPayment'
          testId='invoiceHub-markAsPaid-recordAPayment'
          onClick={handleSubmit}
          title={t('INVOICE_HUB_MODALS.MARK_AS_PAID.BUTTON_RECORD')}
          className='h-[44px] w-[177px] ml-[12px]'
        />
      </div>
    </div>
  );
};
